import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { RWebShare } from "react-web-share";
import { trackPhoneCall } from "../../utils/analytics";
import "./Header.css";

const NAV_LINKS = [
  { to: "/", label: "Home", icon: "fas fa-home", end: true },
  { to: "/services", label: "Services", icon: "fas fa-tools" },
  { to: "/blog", label: "Blog", icon: "fas fa-newspaper" },
  { to: "/shop", label: "Shop", icon: "fas fa-shopping-bag" },
  { to: "/about-us", label: "About Us", icon: "fas fa-info-circle" },
  { to: "/contact-us", label: "Contact Us", icon: "fas fa-envelope" },
];

const SOCIALS = [
  { href: "https://www.facebook.com/icellstation/", icon: "fab fa-facebook-f", label: "Facebook" },
  { href: "https://www.tiktok.com/@icellstation?lang=en", icon: "fab fa-tiktok", label: "TikTok" },
  {
    href: "https://www.youtube.com/channel/UC_ju-JULprEqMmO3Wlj7DyA",
    icon: "fab fa-youtube",
    label: "YouTube",
  },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Frost + shrink the nav once the page scrolls a little.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <header className={`cs-header${scrolled ? " is-scrolled" : ""}`}>
      {/* ---- Top contact bar ---- */}
      <div className="cs-topbar">
        <div className="container">
          <div className="cs-topbar__inner">
            <div className="cs-topbar__group">
              <a
                href="tel:+12159393933"
                className="cs-topbar__item"
                onClick={() => trackPhoneCall("header_top_bar")}
              >
                <i className="fas fa-phone-alt" />
                +1 (215) 939-3933
              </a>
              <a href="mailto:icellstation1@gmail.com" className="cs-topbar__item">
                <i className="fas fa-envelope" />
                icellstation1@gmail.com
              </a>
              <Link to="/store-location" className="cs-topbar__item">
                <i className="fas fa-map-marker-alt" />
                2500 W Moreland Rd, Willow Grove, PA
              </Link>
            </div>
            <div className="cs-topbar__group">
              <div className="cs-topbar__socials">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="initiate-scripts"
                  >
                    <span className={s.icon} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Main glass nav ---- */}
      <div className="cs-nav">
        <div className="container">
          <div className="cs-nav__inner">
            <Link className="cs-brand initiate-scripts" to="/" onClick={closeDrawer}>
              <img src={process.env.PUBLIC_URL + "/assets/images/logo.jpg"} alt="Cell Station" />
            </Link>

            {/* Desktop links */}
            <ul className="cs-menu">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) =>
                      `cs-menu__link initiate-scripts${isActive ? " active" : ""}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Right actions */}
            <div className="cs-actions">
              <Link to="/quote" className="cs-cta initiate-scripts">
                <i className="fas fa-bolt" />
                Get a Quote
              </Link>

              <RWebShare
                data={{
                  text: "Cell Station — trusted phone & computer repair in Willow Grove, PA.",
                  url: "https://www.icellstation.com/",
                  title: "Cell Station",
                }}
                onClick={() => {}}
              >
                <button className="cs-iconbtn" aria-label="Share">
                  <span className="fas fa-share-alt" />
                </button>
              </RWebShare>

              <Link to="/shop" className="cs-iconbtn initiate-scripts" aria-label="Shop">
                <span className="fas fa-shopping-bag" />
              </Link>

              {/* Hamburger (mobile) */}
              <button
                className={`cs-burger${drawerOpen ? " is-open" : ""}`}
                aria-label="Menu"
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen((o) => !o)}
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Mobile drawer ---- */}
      <div
        className={`cs-drawer-overlay${drawerOpen ? " is-open" : ""}`}
        onClick={closeDrawer}
      />
      <aside className={`cs-drawer${drawerOpen ? " is-open" : ""}`} aria-hidden={!drawerOpen}>
        <div className="cs-drawer__head">
          <img src={process.env.PUBLIC_URL + "/assets/images/logo.jpg"} alt="Cell Station" />
          <button className="cs-drawer__close" onClick={closeDrawer} aria-label="Close menu">
            <i className="fas fa-times" />
          </button>
        </div>

        <ul className="cs-drawer__links">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.end}
                onClick={closeDrawer}
                className={({ isActive }) => (isActive ? "active" : undefined)}
              >
                <i className={l.icon} />
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <Link to="/quote" className="cs-drawer__cta" onClick={closeDrawer}>
          <i className="fas fa-bolt" />
          Get a Free Quote
        </Link>

        <div className="cs-drawer__foot">
          <a href="tel:+12159393933" onClick={() => trackPhoneCall("mobile_drawer")}>
            <i className="fas fa-phone-alt" />
            +1 (215) 939-3933
          </a>
          <a href="mailto:icellstation1@gmail.com">
            <i className="fas fa-envelope" />
            icellstation1@gmail.com
          </a>
          <div className="cs-drawer__social">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
              >
                <span className={s.icon} />
              </a>
            ))}
          </div>
        </div>
      </aside>
    </header>
  );
};

export default Header;
